/**
 * Unmatched-rows panel for a batch. When a raw row's `raw_account_name`
 * doesn't match any alias, the user MUST classify it before verification.
 *
 * Three actions:
 *   - Attach to existing account (pick from dropdown)
 *   - Create new account and attach (inline form)
 *   - Mark ignored with a reason
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account, Party, RawUplineRow } from '../api';
import {
  Button,
  Card,
  ErrorBanner,
  Select,
  Spinner,
  TextInput,
} from '../ui/primitives';

export function UnmatchedPanel({ batchId }: { batchId: number }) {
  const qc = useQueryClient();
  const unmatched = useQuery<RawUplineRow[]>({
    queryKey: ['batch', batchId, 'unmatched'],
    queryFn: () => api.get<RawUplineRow[]>(`/api/batches/${batchId}/raw-rows/unmatched`),
  });

  if (unmatched.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-small text-ink-muted">
          <Spinner /> loading…
        </div>
      </Card>
    );
  }
  if (!unmatched.data || unmatched.data.length === 0) {
    return null; // nothing to resolve — don't clutter the UI
  }

  const rematch = () => {
    void api.post(`/api/batches/${batchId}/rematch`).then(() => {
      void qc.invalidateQueries();
    });
  };

  return (
    <Card className="border-amber-200 bg-amber-50">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-small font-semibold text-amber-700">
            Unmatched raw rows ({unmatched.data.length})
          </h3>
          <p className="mt-1 text-caption text-amber-600">
            Each row below has a name the app has never seen. Classify it
            once and future imports will resolve automatically.
          </p>
        </div>
        <Button variant="ghost" onClick={rematch}>
          Re-run match
        </Button>
      </div>
      <ul className="space-y-3">
        {unmatched.data.map((row) => (
          <UnmatchedRow key={row.id} row={row} onResolved={() => void qc.invalidateQueries()} />
        ))}
      </ul>
    </Card>
  );
}

function UnmatchedRow({
  row,
  onResolved,
}: {
  row: RawUplineRow;
  onResolved: () => void;
}) {
  const [mode, setMode] = useState<'attach' | 'create' | 'ignore'>('attach');
  const [accountId, setAccountId] = useState('');
  const [newAccountName, setNewAccountName] = useState(() => cleanNameGuess(row.rawAccountName));
  const [newAccountUplineId, setNewAccountUplineId] = useState('');
  const [newAccountPanel, setNewAccountPanel] = useState(row.panel ?? '');
  const [ignoreReason, setIgnoreReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const accounts = useQuery<Account[]>({
    queryKey: ['accounts'],
    queryFn: () => api.get<Account[]>('/api/accounts'),
  });
  const uplines = useQuery<Party[]>({
    queryKey: ['parties', 'upline'],
    queryFn: () => api.get<Party[]>('/api/parties?role=upline'),
  });

  const attachMut = useMutation({
    mutationFn: async () => {
      if (mode === 'attach') {
        if (!accountId) throw new Error('Pick an account.');
        await api.post(`/api/accounts/${accountId}/aliases`, {
          rawName: row.rawAccountName,
        });
      } else if (mode === 'create') {
        if (!newAccountUplineId) throw new Error('Pick an upline.');
        if (!newAccountName.trim()) throw new Error('Canonical name is required.');
        const acct = await api.post<Account>('/api/accounts', {
          uplinePartyId: Number(newAccountUplineId),
          canonicalName: newAccountName.trim(),
          panel: newAccountPanel.trim() || null,
        });
        await api.post(`/api/accounts/${acct.id}/aliases`, {
          rawName: row.rawAccountName,
        });
      } else {
        // ignore
        if (!ignoreReason.trim()) throw new Error('Reason is required for ignore.');
        await api.post('/api/aliases/ignore', {
          rawName: row.rawAccountName,
          reason: ignoreReason.trim(),
        });
      }
      // After classifying, re-run match on this batch to update matched_account_id.
      await api.post(`/api/batches/${row.batchId}/rematch`);
    },
    onSuccess: () => {
      setErr(null);
      onResolved();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const accountOptions = (accounts.data ?? []).map((a) => ({
    value: String(a.id),
    label: `${a.canonicalName}${a.panel ? ` · ${a.panel}` : ''}`,
  }));
  const uplineOptions = (uplines.data ?? []).map((u) => ({
    value: String(u.id),
    label: u.displayName,
  }));

  return (
    <li className="rounded-md border border-border bg-surface p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-small">
          <span className="font-medium text-ink">{row.rawAccountName}</span>
          {row.panel && (
            <span className="ml-2 text-caption text-ink-muted">{row.panel}</span>
          )}
          <span className="ml-2 text-caption text-ink-muted">
            {row.pointsStated} × {row.rateStated} = {row.amountStated}
          </span>
        </div>
      </div>
      <div className="mb-2 flex gap-1 text-xs">
        <button
          className={tabBtn(mode === 'attach')}
          onClick={() => setMode('attach')}
        >
          Attach to existing
        </button>
        <button
          className={tabBtn(mode === 'create')}
          onClick={() => setMode('create')}
        >
          Create new account
        </button>
        <button
          className={tabBtn(mode === 'ignore')}
          onClick={() => setMode('ignore')}
        >
          Ignore
        </button>
      </div>

      {mode === 'attach' && (
        <Select
          label="Existing account"
          value={accountId}
          onChange={setAccountId}
          options={accountOptions}
          placeholder="Choose…"
        />
      )}

      {mode === 'create' && (
        <div className="grid gap-2 sm:grid-cols-3">
          <Select
            label="Upline"
            value={newAccountUplineId}
            onChange={setNewAccountUplineId}
            options={uplineOptions}
            placeholder="Choose…"
          />
          <TextInput
            label="Canonical name"
            value={newAccountName}
            onChange={(e) => setNewAccountName(e.target.value)}
          />
          <TextInput
            label="Panel"
            value={newAccountPanel}
            onChange={(e) => setNewAccountPanel(e.target.value)}
          />
        </div>
      )}

      {mode === 'ignore' && (
        <TextInput
          label="Reason (required)"
          value={ignoreReason}
          onChange={(e) => setIgnoreReason(e.target.value)}
          placeholder="e.g. supplier's own test account"
        />
      )}

      {err && (
        <div className="mt-2">
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}

      <div className="mt-3">
        <Button onClick={() => attachMut.mutate()} disabled={attachMut.isPending}>
          {attachMut.isPending ? <Spinner /> : 'Apply'}
        </Button>
      </div>
    </li>
  );
}

function tabBtn(active: boolean): string {
  return [
    'rounded-md px-3 py-1.5 text-caption transition-colors',
    active
      ? 'bg-brand text-white'
      : 'bg-surface-raised text-ink-muted ring-1 ring-border hover:bg-surface-raised/80 hover:text-ink',
  ].join(' ');
}

function cleanNameGuess(raw: string): string {
  // "S ADMIN (LOTUS365AURA)" → "Lotus365Aura"
  const m = /\(([^)]+)\)\s*$/.exec(raw);
  if (m?.[1]) {
    const v = m[1].trim();
    return v.charAt(0).toUpperCase() + v.slice(1);
  }
  return raw.trim();
}
